import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  fetchReportingScope,
  fetchReportingRawTickets,
  fetchReportingSubtypes,
  fetchReportingCategories,
  fetchReportingResolutionByType,
} from '@/hooks/useReporting';
import {
  TICKET_TYPES,
  PRIORITIES,
  REFERENCE_SLA,
  type TicketTypeFilter,
  type Priority,
  type ReportingScope,
  type RawTicket,
  type SubtypeCountRow,
  type CategoryBreakdownRow,
  type ResByTypeRow,
  type Granularity,
  type DayModeFilter,
} from '@/components/reporting/types';
import { BarChart } from '@/components/reporting/BarChart';
import { HorizontalBarChart } from '@/components/reporting/HorizontalBarChart';
import { StatTile } from '@/components/reporting/StatTile';
import { SERIES_COLORS, STATUS_COLORS } from '@/components/reporting/chartColors';
import { makeHoursBetween, toDays } from '@/lib/businessDays';
import {
  aggregateDaily,
  resolvedHours,
  respondedHours,
  avgByBucket,
  medianByBucket,
  p70ByBucket,
  p90ByBucket,
  computeSlaStats,
  openPastDeadlineByPriority,
  pct,
  fmtHours,
  type SlaTargets,
} from '@/components/reporting/reportingMath';
import { median, p70, p90, mean } from '@/lib/businessDays';

const GRANULARITIES: Granularity[] = ['day', 'week', 'month', 'year'];

function defaultSimDraft() {
  const toStr = (h: number) => String(h / 24);
  return {
    response: Object.fromEntries(PRIORITIES.map((p) => [p, toStr(REFERENCE_SLA.response[p])])) as Record<Priority, string>,
    resolution: Object.fromEntries(PRIORITIES.map((p) => [p, toStr(REFERENCE_SLA.resolution[p])])) as Record<Priority, string>,
  };
}

function draftToTargets(draft: ReturnType<typeof defaultSimDraft>): SlaTargets {
  const toHours = (v: string) => (Number(v) || 0) * 24;
  return {
    response: Object.fromEntries(PRIORITIES.map((p) => [p, toHours(draft.response[p])])) as SlaTargets['response'],
    resolution: Object.fromEntries(PRIORITIES.map((p) => [p, toHours(draft.resolution[p])])) as SlaTargets['resolution'],
  };
}

function pctWithCount(n: number, total: number): string {
  return `${pct(n, total)} (${n}/${total})`;
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="gradient-band px-6 py-3">
        <h3 className="text-sm font-bold text-white uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

function PillButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'px-3 py-1.5 text-xs font-semibold rounded-full bg-brand-rose text-white'
          : 'px-3 py-1.5 text-xs font-medium rounded-full border border-slate-200 text-slate-600 hover:bg-slate-50'
      }
    >
      {children}
    </button>
  );
}

export function ReportingPage() {
  const { t } = useTranslation(['reporting', 'common']);

  // ── Filters ──────────────────────────────────────────────
  const [ticketType, setTicketType] = useState<TicketTypeFilter>('ALL');
  const [subtype, setSubtype] = useState('');
  const [priority, setPriority] = useState('');
  const [startDateInput, setStartDateInput] = useState('');
  const [endDateInput, setEndDateInput] = useState('');
  const [appliedPeriod, setAppliedPeriod] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [dayMode, setDayMode] = useState<DayModeFilter>('all');
  const [granularity, setGranularity] = useState<Granularity>('week');
  const [simDraft, setSimDraft] = useState(defaultSimDraft());
  const [simTargets, setSimTargets] = useState<SlaTargets>(() => draftToTargets(defaultSimDraft()));

  // ── Data ─────────────────────────────────────────────────
  const [scope, setScope] = useState<ReportingScope | null>(null);
  const [rawTickets, setRawTickets] = useState<RawTicket[]>([]);
  const [subtypeOptionsByType, setSubtypeOptionsByType] = useState<Record<string, SubtypeCountRow[]>>({});
  const [categoryBreakdown, setCategoryBreakdown] = useState<CategoryBreakdownRow[]>([]);
  const [resByType, setResByType] = useState<ResByTypeRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReportingCategories().then((r) => setCategoryBreakdown(r.data)).catch(() => {});
    fetchReportingResolutionByType().then((r) => setResByType(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (ticketType === 'ALL' || subtypeOptionsByType[ticketType]) return;
    fetchReportingSubtypes(ticketType).then((r) => {
      setSubtypeOptionsByType((prev) => ({ ...prev, [ticketType]: r.data }));
    }).catch(() => {});
  }, [ticketType, subtypeOptionsByType]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const query = {
      ticketType: ticketType === 'ALL' ? undefined : ticketType,
      subtype: subtype || undefined,
      priority: priority || undefined,
      startDate: appliedPeriod.start || undefined,
      endDate: appliedPeriod.end || undefined,
    };
    Promise.all([fetchReportingScope(query), fetchReportingRawTickets(query)])
      .then(([scopeRes, rawRes]) => {
        if (cancelled) return;
        setScope(scopeRes.data);
        setRawTickets(rawRes.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [ticketType, subtype, priority, appliedPeriod]);

  // ── Derived (client-side) math ───────────────────────────
  const referenceYears = useMemo(() => {
    const years = new Set<number>();
    years.add(new Date().getUTCFullYear());
    rawTickets.forEach((tk) => {
      years.add(new Date(tk.createdAt).getUTCFullYear());
      if (tk.closedAt) years.add(new Date(tk.closedAt).getUTCFullYear());
    });
    return Array.from(years);
  }, [rawTickets]);

  const hoursBetween = useMemo(() => makeHoursBetween(dayMode, referenceYears), [dayMode, referenceYears]);

  const periodStartDate = appliedPeriod.start ? new Date(`${appliedPeriod.start}T00:00:00Z`) : null;
  const periodEndDate = appliedPeriod.end ? new Date(`${appliedPeriod.end}T23:59:59Z`) : null;

  const resHoursAll = useMemo(() => resolvedHours(rawTickets, hoursBetween), [rawTickets, hoursBetween]);
  const respHoursAll = useMemo(() => respondedHours(rawTickets, hoursBetween), [rawTickets, hoursBetween]);

  const slaStats = useMemo(
    () => computeSlaStats(rawTickets, simTargets, hoursBetween, new Date()),
    [rawTickets, simTargets, hoursBetween],
  );

  const slaStatsByPriority = useMemo(
    () =>
      PRIORITIES.map((p) => ({
        priority: p,
        stats: computeSlaStats(rawTickets.filter((tk) => tk.priority === p), simTargets, hoursBetween, new Date()),
      })),
    [rawTickets, simTargets, hoursBetween],
  );

  const agg = useMemo(
    () => aggregateDaily(scope?.weeklyRows ?? [], granularity, periodStartDate, periodEndDate),
    [scope, granularity, appliedPeriod],
  );
  const bucketKeys = agg.map((b) => b.key);
  const labels = agg.map((b) => b.label);

  const totalTickets = scope?.statusRows.reduce((s, r) => s + r.count, 0) ?? 0;
  const openCount =
    scope?.statusRows.filter((r) => ['OPEN', 'IN_PROGRESS', 'REOPENED'].includes(r.status)).reduce((s, r) => s + r.count, 0) ?? 0;

  const currentSubtypeOptions = ticketType === 'ALL' ? [] : subtypeOptionsByType[ticketType] ?? [];

  const resByPriorityComputed = PRIORITIES.map((p) => {
    const hrs = resolvedHours(rawTickets.filter((tk) => tk.priority === p), hoursBetween);
    return { priority: p, avg: toDays(mean(hrs), dayMode), median: toDays(median(hrs), dayMode), p70: toDays(p70(hrs), dayMode), p90: toDays(p90(hrs), dayMode) };
  });
  const respByPriorityComputed = PRIORITIES.map((p) => {
    const hrs = respondedHours(rawTickets.filter((tk) => tk.priority === p), hoursBetween);
    return { priority: p, avg: toDays(mean(hrs), dayMode), median: toDays(median(hrs), dayMode), p70: toDays(p70(hrs), dayMode), p90: toDays(p90(hrs), dayMode) };
  });
  const resOpenPastSlaByPriority = useMemo(
    () => openPastDeadlineByPriority(rawTickets, simTargets.resolution, hoursBetween, new Date(), 'resolution'),
    [rawTickets, simTargets, hoursBetween],
  );
  const respOpenPastSlaByPriority = useMemo(
    () => openPastDeadlineByPriority(rawTickets, simTargets.response, hoursBetween, new Date(), 'response'),
    [rawTickets, simTargets, hoursBetween],
  );

  const histMaxDays = Math.min(30, Math.max(5, Math.ceil(Math.max(p90(resHoursAll) || 0, p90(respHoursAll) || 0) / 24)));
  function histogram(hoursArr: number[]): number[] {
    const counts = new Array(histMaxDays + 1).fill(0);
    for (const h of hoursArr) {
      const d = Math.floor(h / 24);
      counts[d >= histMaxDays ? histMaxDays : d]++;
    }
    return counts;
  }
  const histLabels = Array.from({ length: histMaxDays + 1 }, (_, i) => (i === histMaxDays ? `${histMaxDays}d+` : `${i}d`));

  const openTickets = rawTickets.filter((t) => !t.closedAt);
  const openAgeDays = openTickets.map((t) => hoursBetween(t.createdAt, new Date().toISOString()) / (dayMode === 'business' ? 8 : 24));
  const openHistMaxDays = Math.min(60, Math.max(5, Math.ceil(Math.max(0, ...openAgeDays))));
  const openHistLabels = Array.from({ length: openHistMaxDays + 1 }, (_, i) => (i === openHistMaxDays ? `${openHistMaxDays}d+` : `${i}d`));
  const openHistCounts = (() => {
    const counts = new Array(openHistMaxDays + 1).fill(0);
    for (const d of openAgeDays) {
      const bucket = Math.floor(d);
      counts[bucket >= openHistMaxDays ? openHistMaxDays : bucket]++;
    }
    return counts;
  })();

  const categoriesByType = useMemo(() => {
    const map: Record<string, CategoryBreakdownRow[]> = {};
    for (const r of categoryBreakdown) {
      (map[r.ticket_type] ??= []).push(r);
    }
    return map;
  }, [categoryBreakdown]);

  function applyPeriod() {
    setAppliedPeriod({ start: startDateInput, end: endDateInput });
  }
  function clearPeriod() {
    setStartDateInput('');
    setEndDateInput('');
    setAppliedPeriod({ start: '', end: '' });
  }
  function applySim() {
    setSimTargets(draftToTargets(simDraft));
  }
  function resetSim() {
    const d = defaultSimDraft();
    setSimDraft(d);
    setSimTargets(draftToTargets(d));
  }

  const dayModeLabel = dayMode === 'business' ? t('reporting:dayMode.businessUnit', 'business days') : t('reporting:dayMode.allUnit', 'days');

  return (
    <div className="space-y-8">
      <div className="gradient-band rounded-xl p-6 text-white">
        <h2 className="text-xl font-bold">{t('reporting:title', 'Reporting')}</h2>
        <p className="text-white/70 text-sm mt-1">{t('reporting:subtitle', 'Ticket volume, SLA, and resolution-time analytics')}</p>
      </div>

      {/* Ticket type tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        {(['ALL', ...TICKET_TYPES] as TicketTypeFilter[]).map((tt) => (
          <button
            key={tt}
            type="button"
            onClick={() => {
              setTicketType(tt);
              setSubtype('');
            }}
            className={
              ticketType === tt
                ? 'px-4 py-2.5 text-sm font-bold text-brand-rose border-b-2 border-brand-rose'
                : 'px-4 py-2.5 text-sm font-medium text-slate-500 hover:text-slate-700'
            }
          >
            {tt === 'ALL' ? t('reporting:allTypes', 'All types') : t(`common:ticketTypeShort.${tt}`, tt)}
          </button>
        ))}
      </div>

      {/* Subtype pills */}
      {ticketType !== 'ALL' && currentSubtypeOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <PillButton active={subtype === ''} onClick={() => setSubtype('')}>
            {t('reporting:allSubtypes', 'All subtypes')}
          </PillButton>
          {currentSubtypeOptions.map((s) => (
            <PillButton key={s.category} active={subtype === s.category} onClick={() => setSubtype(s.category)}>
              {s.category}
            </PillButton>
          ))}
        </div>
      )}

      {/* Priority pills */}
      <div className="flex flex-wrap gap-2">
        <PillButton active={priority === ''} onClick={() => setPriority('')}>
          {t('reporting:allPriorities', 'All priorities')}
        </PillButton>
        {PRIORITIES.map((p) => (
          <PillButton key={p} active={priority === p} onClick={() => setPriority(p)}>
            {p}
          </PillButton>
        ))}
      </div>

      {/* Day mode */}
      <div className="flex flex-wrap gap-2">
        <PillButton active={dayMode === 'all'} onClick={() => setDayMode('all')}>
          {t('reporting:dayMode.all', 'All days')}
        </PillButton>
        <PillButton active={dayMode === 'business'} onClick={() => setDayMode('business')}>
          {t('reporting:dayMode.business', 'Business days (Mon–Fri, 09:00–17:00, BE holidays excluded)')}
        </PillButton>
      </div>

      {/* Period filter */}
      <div className="flex flex-wrap items-end gap-3 bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400" htmlFor="reporting-start-date">{t('reporting:period.start', 'Start date')}</label>
          <input
            id="reporting-start-date"
            type="date"
            value={startDateInput}
            onChange={(e) => setStartDateInput(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-400" htmlFor="reporting-end-date">{t('reporting:period.end', 'End date')}</label>
          <input
            id="reporting-end-date"
            type="date"
            value={endDateInput}
            onChange={(e) => setEndDateInput(e.target.value)}
            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5"
          />
        </div>
        <button type="button" onClick={applyPeriod} className="px-4 py-2 text-xs font-semibold rounded-lg bg-brand-rose text-white">
          {t('reporting:period.apply', 'Apply period')}
        </button>
        <button type="button" onClick={clearPeriod} className="px-4 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-600">
          {t('reporting:period.clear', 'Clear')}
        </button>
        <p className="text-xs text-slate-400 basis-full">
          {t(
            'reporting:period.note',
            'Filters tickets by creation date. Weekly trend charts only have data for the last 90 days server-side; status/assignee/operator breakdowns always reflect all-time state.',
          )}
        </p>
      </div>

      {loading && <p className="text-sm text-slate-400">{t('common:loading', 'Loading…')}</p>}

      {/* Overview */}
      <SectionCard title={t('reporting:sections.overview', 'Overview')}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatTile label={t('reporting:tiles.total', 'Total tickets')} value={totalTickets} />
          <StatTile label={t('reporting:tiles.open', 'Currently open')} value={openCount} sub={pct(openCount, totalTickets) + ' of total'} />
          <StatTile label={t('reporting:tiles.pastDeadline', 'Open past SLA deadline')} value={slaStats.resolution.openPastDeadline} />
        </div>
      </SectionCard>

      {/* Volume & status */}
      <SectionCard title={t('reporting:sections.volume', 'Volume & status')}>
        <div className={`grid grid-cols-1 ${ticketType !== 'ALL' ? 'lg:grid-cols-2' : ''} gap-6`}>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t('reporting:charts.byStatus', 'Tickets by status')}</p>
            <BarChart
              labels={scope?.statusRows.map((r) => r.status) ?? []}
              series={[
                {
                  label: 'Count',
                  color: SERIES_COLORS[0],
                  data: scope?.statusRows.map((r) => r.count) ?? [],
                  colors: scope?.statusRows.map((r) => STATUS_COLORS[r.status.toLowerCase()] ?? SERIES_COLORS[0]),
                },
              ]}
              formatValue={(v) => String(Math.round(v))}
              showLegend={false}
            />
          </div>
          {ticketType !== 'ALL' && (
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t('reporting:charts.byCategory', 'By category / subtype')}</p>
              <HorizontalBarChart rows={currentSubtypeOptions.map((s) => ({ label: s.category, value: s.count }))} />
            </div>
          )}
        </div>
      </SectionCard>

      {/* Trends */}
      <SectionCard title={t('reporting:sections.trends', 'Trends (last 90 days)')}>
        <div className="flex flex-wrap gap-2 mb-4">
          {GRANULARITIES.map((g) => (
            <PillButton key={g} active={granularity === g} onClick={() => setGranularity(g)}>
              {t(`reporting:granularity.${g}`, g)}
            </PillButton>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.createdResolved', 'Created vs resolved vs open backlog')}
            </p>
            <BarChart
              labels={labels}
              formatValue={(v) => String(Math.round(v))}
              series={[
                { label: t('reporting:series.created', 'Created'), color: SERIES_COLORS[0], data: agg.map((b) => b.created_count) },
                { label: t('reporting:series.resolved', 'Resolved'), color: SERIES_COLORS[1], data: agg.map((b) => b.resolved_count) },
                { label: t('reporting:series.openBacklog', 'Open backlog'), color: SERIES_COLORS[2], data: agg.map((b) => b.open_backlog) },
              ]}
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t('reporting:charts.statusMix', 'Status mix of tickets created')}</p>
            <BarChart
              labels={labels}
              stacked
              formatValue={(v) => String(Math.round(v))}
              series={[
                { label: 'open', color: STATUS_COLORS.open, data: agg.map((b) => b.open_count) },
                { label: 'in progress', color: STATUS_COLORS.in_progress, data: agg.map((b) => b.in_progress_count) },
                { label: 'closed', color: STATUS_COLORS.closed, data: agg.map((b) => b.closed_count) },
                { label: 'reopened', color: STATUS_COLORS.reopened, data: agg.map((b) => b.reopened_count) },
              ]}
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.responseTime', 'Avg / median response time')} ({dayModeLabel})
            </p>
            <BarChart
              labels={labels}
              formatValue={(v) => v.toFixed(1)}
              series={[
                { label: 'Avg', color: SERIES_COLORS[0], data: avgByBucket(rawTickets, bucketKeys, granularity, 'firstResponseAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: 'Median', color: SERIES_COLORS[1], data: medianByBucket(rawTickets, bucketKeys, granularity, 'firstResponseAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: '70% within', color: SERIES_COLORS[2], data: p70ByBucket(rawTickets, bucketKeys, granularity, 'firstResponseAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: '90% within', color: SERIES_COLORS[3], data: p90ByBucket(rawTickets, bucketKeys, granularity, 'firstResponseAt', hoursBetween).map((v) => toDays(v, dayMode)) },
              ]}
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.resolutionTime', 'Avg / median resolution time')} ({dayModeLabel})
            </p>
            <BarChart
              labels={labels}
              formatValue={(v) => v.toFixed(1)}
              series={[
                { label: 'Avg', color: SERIES_COLORS[0], data: avgByBucket(rawTickets, bucketKeys, granularity, 'closedAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: 'Median', color: SERIES_COLORS[1], data: medianByBucket(rawTickets, bucketKeys, granularity, 'closedAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: '70% within', color: SERIES_COLORS[2], data: p70ByBucket(rawTickets, bucketKeys, granularity, 'closedAt', hoursBetween).map((v) => toDays(v, dayMode)) },
                { label: '90% within', color: SERIES_COLORS[3], data: p90ByBucket(rawTickets, bucketKeys, granularity, 'closedAt', hoursBetween).map((v) => toDays(v, dayMode)) },
              ]}
            />
          </div>
        </div>
      </SectionCard>

      {/* SLA what-if simulator */}
      <SectionCard title={t('reporting:sections.whatIf', 'SLA what-if simulator')}>
        <p className="text-xs text-slate-400 mb-4">
          {t(
            'reporting:whatIf.note',
            'All SLA figures on this page (breach rate, met %) are computed against these targets — the per-ticket-type SLA config stored in UniTicket is not used. Response time is a derived proxy: earliest ticket-activity row from a Unifiber user (SYSTEM_ADMIN / UNIFIBER_OPERATOR role), not the app\'s own stored flag — it never records a real first-response event.',
          )}
        </p>
        <div className="flex flex-wrap gap-4 mb-4">
          {PRIORITIES.map((p) => (
            <div key={`resp-${p}`} className="flex flex-col gap-1">
              <label className="text-xs text-slate-400" htmlFor={`resp-${p}`}>{p} response (d)</label>
              <input
                id={`resp-${p}`}
                type="number"
                min={0.1}
                step={0.1}
                value={simDraft.response[p]}
                onChange={(e) => setSimDraft((d) => ({ ...d, response: { ...d.response, [p]: e.target.value } }))}
                className="w-24 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
              />
            </div>
          ))}
          {PRIORITIES.map((p) => (
            <div key={`res-${p}`} className="flex flex-col gap-1">
              <label className="text-xs text-slate-400" htmlFor={`res-${p}`}>{p} resolution (d)</label>
              <input
                id={`res-${p}`}
                type="number"
                min={0.1}
                step={0.1}
                value={simDraft.resolution[p]}
                onChange={(e) => setSimDraft((d) => ({ ...d, resolution: { ...d.resolution, [p]: e.target.value } }))}
                className="w-24 text-sm border border-slate-200 rounded-lg px-2 py-1.5"
              />
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={applySim} className="px-4 py-2 text-xs font-semibold rounded-lg bg-brand-rose text-white">
            {t('reporting:whatIf.apply', 'Apply')}
          </button>
          <button type="button" onClick={resetSim} className="px-4 py-2 text-xs font-medium rounded-lg border border-slate-200 text-slate-600">
            {t('reporting:whatIf.reset', 'Reset to reference SLA')}
          </button>
        </div>
      </SectionCard>

      {/* SLA & resolution time */}
      <SectionCard title={t('reporting:sections.sla', 'SLA & resolution time')}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatTile
            label={t('reporting:tiles.responseMet', 'Response SLA met')}
            value={pctWithCount(slaStats.response.met, slaStats.response.met + slaStats.response.missed)}
          />
          <StatTile
            label={t('reporting:tiles.resolutionMet', 'Resolution SLA met')}
            value={pctWithCount(slaStats.resolution.resolved - slaStats.resolution.breached, slaStats.resolution.resolved)}
          />
        </div>
        <table className="w-full text-xs mb-6">
          <thead>
            <tr className="text-left text-slate-400 border-b border-slate-100">
              <th className="py-1">{t('reporting:tables.priority', 'Priority')}</th>
              <th className="py-1">{t('reporting:tiles.responseMet', 'Response SLA met')}</th>
              <th className="py-1">{t('reporting:tiles.resolutionMet', 'Resolution SLA met')}</th>
            </tr>
          </thead>
          <tbody>
            {slaStatsByPriority.map(({ priority, stats }) => (
              <tr key={priority} className="border-b border-slate-50">
                <td className="py-1">{priority}</td>
                <td className="py-1 tabular-nums font-semibold">{pctWithCount(stats.response.met, stats.response.met + stats.response.missed)}</td>
                <td className="py-1 tabular-nums font-semibold">{pctWithCount(stats.resolution.resolved - stats.resolution.breached, stats.resolution.resolved)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.responseByPriority', 'Avg / median response time by priority')} ({dayModeLabel})
            </p>
            <BarChart
              labels={respByPriorityComputed.map((r) => r.priority)}
              formatValue={(v) => v.toFixed(1)}
              series={[
                { label: 'Avg', color: SERIES_COLORS[0], data: respByPriorityComputed.map((r) => r.avg) },
                { label: 'Median', color: SERIES_COLORS[1], data: respByPriorityComputed.map((r) => r.median) },
                { label: '70% within', color: SERIES_COLORS[2], data: respByPriorityComputed.map((r) => r.p70) },
                { label: '90% within', color: SERIES_COLORS[3], data: respByPriorityComputed.map((r) => r.p90) },
              ]}
            />
            <table className="w-full text-xs mt-8">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-1">{t('reporting:tables.priority', 'Priority')}</th>
                  <th className="py-1">{t('reporting:tables.openPastSla', 'Open past SLA')}</th>
                </tr>
              </thead>
              <tbody>
                {PRIORITIES.map((p) => (
                  <tr key={p} className="border-b border-slate-50">
                    <td className="py-1">{p}</td>
                    <td className="py-1 tabular-nums font-semibold">{respOpenPastSlaByPriority[p]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.resolutionByPriority', 'Avg / median resolution time by priority')} ({dayModeLabel})
            </p>
            <BarChart
              labels={resByPriorityComputed.map((r) => r.priority)}
              formatValue={(v) => v.toFixed(1)}
              series={[
                { label: 'Avg', color: SERIES_COLORS[0], data: resByPriorityComputed.map((r) => r.avg) },
                { label: 'Median', color: SERIES_COLORS[1], data: resByPriorityComputed.map((r) => r.median) },
                { label: '70% within', color: SERIES_COLORS[2], data: resByPriorityComputed.map((r) => r.p70) },
                { label: '90% within', color: SERIES_COLORS[3], data: resByPriorityComputed.map((r) => r.p90) },
              ]}
            />
            <table className="w-full text-xs mt-8">
              <thead>
                <tr className="text-left text-slate-400 border-b border-slate-100">
                  <th className="py-1">{t('reporting:tables.priority', 'Priority')}</th>
                  <th className="py-1">{t('reporting:tables.openPastSla', 'Open past SLA')}</th>
                </tr>
              </thead>
              <tbody>
                {PRIORITIES.map((p) => (
                  <tr key={p} className="border-b border-slate-50">
                    <td className="py-1">{p}</td>
                    <td className="py-1 tabular-nums font-semibold">{resOpenPastSlaByPriority[p]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.distribution', 'Response & resolution time distribution')}
            </p>
            <BarChart
              labels={histLabels}
              formatValue={(v) => String(Math.round(v))}
              series={[
                { label: 'Response', color: SERIES_COLORS[0], data: histogram(respHoursAll) },
                { label: 'Resolution', color: SERIES_COLORS[1], data: histogram(resHoursAll) },
              ]}
            />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
              {t('reporting:charts.openAge', 'Days open (currently open tickets)')} ({dayModeLabel})
            </p>
            <BarChart
              labels={openHistLabels}
              formatValue={(v) => String(Math.round(v))}
              showLegend={false}
              series={[{ label: t('reporting:series.openTickets', 'Open tickets'), color: SERIES_COLORS[4], data: openHistCounts }]}
            />
          </div>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
            {ticketType === 'ALL'
              ? t('reporting:tables.resByType', 'Avg resolution time by ticket type (all types)')
              : t('reporting:tables.resByTypeFiltered', 'Avg resolution time by ticket type')}
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="py-1.5">{t('reporting:tables.ticketType', 'Ticket type')}</th>
                <th className="py-1.5">{t('reporting:tables.resolved', 'Resolved')}</th>
                <th className="py-1.5">{t('reporting:tables.avgTime', 'Avg time')}</th>
                <th className="py-1.5">{t('reporting:tables.medianTime', 'Median time')}</th>
              </tr>
            </thead>
            <tbody>
              {resByType
                .filter((r) => ticketType === 'ALL' || r.ticket_type === ticketType)
                .map((r) => (
                <tr key={r.ticket_type} className="border-b border-slate-50">
                  <td className="py-1.5">{t(`common:ticketTypeShort.${r.ticket_type}`, r.ticket_type)}</td>
                  <td className="py-1.5 tabular-nums">{r.resolved_count}</td>
                  <td className="py-1.5 tabular-nums">{fmtHours(r.avg_hours)}</td>
                  <td className="py-1.5 tabular-nums">{fmtHours(r.median_hours)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* By category & assignee */}
      <SectionCard title={t('reporting:sections.categoryAssignee', 'By category & assignee')}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {Object.entries(categoriesByType)
            .filter(([type]) => ticketType === 'ALL' || type === ticketType)
            .map(([type, rows]) => (
              <div key={type}>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t(`common:ticketTypeShort.${type}`, type)}</p>
                <HorizontalBarChart rows={rows.map((r) => ({ label: r.category, value: r.count }))} />
              </div>
            ))}
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t('reporting:tables.byAssignee', 'By assignee')}</p>
            <HorizontalBarChart rows={(scope?.assigneeRows ?? []).map((r) => ({ label: r.assignee, value: r.count }))} />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">{t('reporting:tables.byOperator', 'By operator')}</p>
            <HorizontalBarChart rows={(scope?.operatorRows ?? []).map((r) => ({ label: `${r.operator} (${r.operator_code})`, value: r.count }))} />
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
